import React, {useCallback, useEffect, useState} from "react";
import {Box, Button, CircularProgress, Stack, Switch, Tooltip, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {HugeiconsIcon} from "@hugeicons/react";
import {Settings02Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {ProjectRoles} from "../../context/AppContext";
import {groupCount as countOf, WebrtcRepository, webrtcErrorCode, webrtcErrorMessage} from "../../repositories/WebrtcRepository";
import {useRegistryProject} from "../../pages/registry-project/RegistryProjectContext";

const POLL_MS = 4000;

export const AgentGroupsSection = ({role, onNavigate}) => {
  const {project, basePath} = useRegistryProject();
  const navigate = useNavigate();
  const canManage = [ProjectRoles.ADMIN, ProjectRoles.MANAGER].includes(role);
  const [groups, setGroups] = useState(null);
  const [pending, setPending] = useState(null);
  const projectId = project?.id;

  const load = useCallback(() => WebrtcRepository.getMyGroups({projectId})
    .then(({data}) => setGroups(data?.groups || []))
    .catch(e => console.error("Failed to load the agent groups", e)), [projectId]);

  useEffect(() => {
    if (!projectId) return;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [projectId, load]);

  const toggle = async (group) => {
    const join = !group.joined;
    setPending(group.id);
    try {
      if (join) await WebrtcRepository.joinGroup({projectId, groupId: group.id});
      else await WebrtcRepository.leaveGroup({projectId, groupId: group.id});
      setGroups(current => current.map(item => item.id === group.id ? {...item, joined: join} : item));
      load();
    } catch (e) {
      toast.error(webrtcErrorCode(e) === "agent_not_registered"
        ? "Connect your browser phone once before joining a group"
        : webrtcErrorMessage(e, `Failed to ${join ? "join" : "leave"} "${group.name}"`));
    } finally {
      setPending(null);
    }
  };

  return (
    <Box sx={{pb: 0.5}}>
      <Box sx={{px: 2, pt: 0.75, pb: 0.5}}>
        <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", color: "#8491F7", textTransform: "uppercase"}}>
          Agent groups
        </Typography>
        <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>Calls to the groups you join ring in this browser.</Typography>
      </Box>
      <Stack sx={{px: 1, maxHeight: 260, overflowY: "auto"}}>
        {groups === null && <Box sx={{display: "flex", justifyContent: "center", py: 2}}><CircularProgress size={18}/></Box>}
        {groups?.length === 0 && (
          <Typography sx={{fontSize: 12, color: "#8A8AA0", px: 1, py: 1.5}}>
            {canManage
              ? "No agent groups in this project yet. Create one below."
              : "No agent groups in this project yet. An admin adds them in Settings → Agent Groups."}
          </Typography>
        )}
        {groups?.map(group => (
          <Stack
            key={group.id} direction={"row"} alignItems={"center"} gap={1}
            sx={{px: 1, py: 0.75, borderRadius: "10px", "&:hover": {bgcolor: "#FAFAFD"}}}
          >
            <Box sx={{flex: 1, minWidth: 0}}>
              <Typography noWrap sx={{fontSize: 13, fontWeight: 700, color: "#23264D"}}>
                {group.name}
                <Box component={"span"} sx={{fontWeight: 500, color: "#8A8AA0", fontFamily: "monospace", ml: 0.75}}>
                  {group.number}
                </Box>
              </Typography>
              <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
                <Box component={"span"} sx={{color: Number(group.waiting_count) ? "#C7781E" : "inherit", fontWeight: Number(group.waiting_count) ? 700 : 400}}>
                  {countOf(group.waiting_count)} waiting
                </Box>
                {" · "}{countOf(group.online_count)} online
              </Typography>
            </Box>
            {pending === group.id
              ? <Box sx={{width: 58, display: "flex", justifyContent: "center"}}><CircularProgress size={16}/></Box>
              : (
                <Tooltip title={group.joined ? "Leave" : "Join"}>
                  <Switch
                    size={"small"} checked={Boolean(group.joined)} onChange={() => toggle(group)}
                    inputProps={{"aria-label": `${group.joined ? "Leave" : "Join"} ${group.name}`}}
                  />
                </Tooltip>
              )}
          </Stack>
        ))}
      </Stack>
      {canManage && (
        <Box sx={{px: 1, pt: 0.5}}>
          <Button
            fullWidth startIcon={<HugeiconsIcon icon={Settings02Icon} size={16}/>}
            onClick={() => {
              onNavigate?.();
              navigate(`${basePath}/dialplan?tab=agent-groups`);
            }}
            sx={{justifyContent: "flex-start", textTransform: "none", fontSize: 13, fontWeight: 600, color: "#4B55C9", borderRadius: "10px"}}
          >
            Create and manage groups
          </Button>
        </Box>
      )}
    </Box>
  );
};
